import { isAcTerminalId } from "../ac";
import type { DefLookup } from "../catalog";
import { effectiveTerminals } from "../effective";
import type { ComponentDefinition, ComponentNode, Issue, System, Terminal } from "../types";
import { acCircuitDesignStates } from "./ac-circuits";
import { matchesAcSafetyIdentity } from "./ac-safety-identity";
import { buildGraph, v, type Vertex } from "./graph";

const LEAKAGE_PANELS = new Set(["shore-elci-blue-sea-8100", "breaker-ac-blue-sea-3091", "dist-ac-gfci-leviton-gftr2"]);
// Exact identities only: names/descriptions cannot establish a safety feature.
const RELAY_SOURCES = new Set([
  "shore-victron-multiplus-3000",
  "shore-victron-multiplus-ii-12-3000-230",
  "shore-victron-multiplus-ii-12-3000-2x120",
  "shore-victron-multiplus-ii-48-5000",
]);

type Endpoint = { node: ComponentNode; def: ComponentDefinition; terminal: Terminal };
function pole(terminal: Terminal): string | undefined {
  return isAcTerminalId(terminal.id) ? terminal.id.toLowerCase().split(/[-_]/).at(-1) : undefined;
}

/**
 * This is deliberately a verification boundary, not an AC protection solver.
 * Blue Sea's 8100 is the catalog's explicitly identified leakage device:
 * https://www.bluesea.com/products/8100/ELCI_Main_30A_Double_Pole
 * A generic AC panel, product name or DC neutral-polarity switch does not
 * establish that an RCD is fitted.
 *
 * Honda EU2200i manual 31Z446170 p54 documents a floating factory neutral:
 * https://cdn.powerequipment.honda.com/pe/pdf/manuals/00X31Z446170.pdf
 * MultiPlus-II installation §4.4 documents a mode-dependent grounding relay:
 * https://www.victronenergy.com/media/pg/MultiPlus-II_230V/en/installation.html
 * The persisted drawing has no verified relay/jumper configuration or full
 * source-transfer model. Even a drawn N/PE jumper must retain verification;
 * it is never treated as approval of that bond or offered as an automatic fix.
 */
function detectAcNeutralProtectionInState(system: System, lookup: DefLookup, componentStates: Record<string, string>): Issue[] {
  const graph = buildGraph(system, lookup, { simulate: { componentStates } });
  const endpoints = new Map<Vertex, Endpoint>();
  const panels: Array<{ node: ComponentNode; def: ComponentDefinition }> = [];
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    for (const terminal of effectiveTerminals(def, node)) endpoints.set(v(node.id, terminal.id), { node, def, terminal });
    if (LEAKAGE_PANELS.has(def.id) && matchesAcSafetyIdentity(node, def)) panels.push({ node, def });
  }

  const issues: Issue[] = [];
  for (const { node, def } of panels) {
    const line = v(node.id, "ac-in-l");
    const neutral = v(node.id, "ac-in-n");
    const inputCables = system.cables.filter(cable =>
      [line, neutral].includes(v(cable.fromNodeId, cable.fromTerminalId)) ||
      [line, neutral].includes(v(cable.toNodeId, cable.toTerminalId)));
    if (inputCables.length === 0) continue;

    // Never cross this panel: a load-side N/PE jumper must not masquerade as
    // the supply's bond. Keep L/N separate while identifying a source pair.
    const upstream = (start: Vertex, conductor: "l" | "n"): Set<Vertex> => {
      const seen = new Set([start]);
      const queue = [start];
      for (let i = 0; i < queue.length; i++) {
        const at = queue[i];
        const current = endpoints.get(at);
        // The first source/output boundary owns the supply arrangement. Do
        // not cross a converter or isolation transformer into its own input.
        if (at !== start && current && (current.def.category !== "distribution" ||
          current.def.id === "shore-victron-isolation-transformer-3600")) continue;
        for (const { vertex, via } of graph.adj.get(at) ?? []) {
          if (via.kind === "intra" && via.link.nodeId === node.id) continue;
          const end = endpoints.get(vertex);
          if (!end || pole(end.terminal) !== conductor || seen.has(vertex)) continue;
          // Don't follow cabling back onto a different pole of this panel.
          if (end.node.id === node.id && vertex !== start) continue;
          seen.add(vertex);
          queue.push(vertex);
        }
      }
      return seen;
    };
    const lineReach = upstream(line, "l");
    const neutralReach = upstream(neutral, "n");
    const sources = new Map<string, Endpoint>();
    for (const vertex of lineReach) {
      const end = endpoints.get(vertex)!;
      if (end.node.id === node.id || end.terminal.direction !== "output") continue;
      const isSource = end.def.category === "source" || end.def.category === "charger" ||
        end.def.category === "load" || end.def.id === "shore-victron-isolation-transformer-3600";
      if (!isSource) continue;
      const neutralId = end.terminal.id.replace(/l$/i, "n");
      if (neutralReach.has(v(end.node.id, neutralId))) sources.set(end.node.id, end);
    }

    // Only source-side AC N and PE are considered. Never cross a DC negative,
    // another source, or the protected panel to make a bond appear present.
    const hasDrawnSourceBond = (source: Endpoint): boolean => {
      const start = v(source.node.id, source.terminal.id.replace(/l$/i, "n"));
      const queue = [start];
      const seen = new Set(queue);
      for (let i = 0; i < queue.length; i++) {
        for (const { vertex, via } of graph.adj.get(queue[i]) ?? []) {
          if (via.kind === "intra" && via.link.nodeId === node.id) continue;
          const end = endpoints.get(vertex);
          if (!end || end.node.id === node.id || seen.has(vertex)) continue;
          if (end.terminal.polarity !== "ground" && pole(end.terminal) !== "n") continue;
          if (end.node.id !== source.node.id && end.def.category !== "distribution") continue;
          if (end.node.id === source.node.id && end.terminal.polarity === "ground") return true;
          seen.add(vertex);
          queue.push(vertex);
        }
      }
      return false;
    };

    const label = node.label ?? def.shortName ?? def.name;
    const sourceNames = [...sources.values()].map(source => source.node.label ?? source.def.shortName ?? source.def.name);
    // Unknown/mismatched L/N provenance uses a message that does not invent a
    // source name; wiring completeness remains an independent requirement.
    const vars = { label, source: sourceNames.join(", ") };
    const floating = [...sources.values()].some(source => source.def.id === "src-generator" &&
      matchesAcSafetyIdentity(source.node, source.def) && !hasDrawnSourceBond(source));
    const relay = !floating && [...sources.values()].some(source => RELAY_SOURCES.has(source.def.id) &&
      matchesAcSafetyIdentity(source.node, source.def));
    const code = floating ? "ac-source-neutral-floating" : "ac-neutral-bond-unverified";
    issues.push({
      code, severity: "warning",
      title: { key: `diag.${code}.title`, vars },
      message: { key: floating ? "diag.ac-source-neutral-floating.message" :
        sources.size === 0 ? "diag.ac-neutral-bond-unverified.message_source" :
        relay ? "diag.ac-neutral-bond-unverified.message_relay" : "diag.ac-neutral-bond-unverified.message", vars },
      fix: { key: `diag.${code}.fix` },
      nodeIds: [node.id, ...sources.keys()],
      cableIds: inputCables.map(cable => cable.id),
    });
  }
  return issues;
}

/** Verify every mutually exclusive conducting position; default shore mode
 * cannot hide the generator's floating neutral or an inverter bond relay. */
export function detectAcNeutralProtection(system: System, lookup: DefLookup): Issue[] {
  const components = system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = lookup(node.definitionId, node.customDefinition);
    return def ? [{ node, def, terms: effectiveTerminals(def, node) }] : [];
  });
  const result = new Map<string, Issue>();
  for (const state of acCircuitDesignStates(components).states) {
    for (const issue of detectAcNeutralProtectionInState(system, lookup, state)) {
      const key = `${issue.code}:${issue.nodeIds?.join("|")}:${issue.message.key}`;
      if (!result.has(key)) result.set(key, issue);
    }
  }
  return [...result.values()];
}
